import React, { FC, ReactNode, useMemo } from 'react'

import { Skeleton } from 'antd'
import { SkeletonNodeProps } from 'antd/es/skeleton/Node'
import styled, { css } from 'styled-components'

import InfoIcon from 'src/Assets/Svg/common/info.svg?react'
import avatar from 'src/Assets/Png/ava.png'
import { SmartImage } from 'src/Components/SmartImage'
import { PlatformButton } from 'src/Components/Buttons/PlatformButton'
import { TaskForCreators } from 'src/Components/TaskForCreators'
import { VideoStatusBadge } from 'src/Components/VideoStatus'
import { PremoderationStatus, VideoStatus } from 'src/Constants/enums'
import { tasks } from 'src/Constants/tikTokTasks'
import { addSpaceToNumber } from 'src/Helpers/layoutHelpers'
import { EventDismissReasonsEnum } from 'src/Redux/creatorSubmittedSoundsApi'
import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'

import { RateBlock, RateFormData } from '../RateBlock'
import { ViewsProgressBar } from '../ViewsProgressBar'

export const AvatarImage = styled(SmartImage)`
  border-radius: 50%;
  height: 40px;
  width: 40px;
  object-fit: cover;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`

const CardStyles = css`
  border-radius: 16px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background-color: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
`

const HeaderWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  ${CardStyles}
`

const VideoDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
  ${CardStyles}

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    box-shadow: none;
    border: none;
    padding: 0;
  }
`

const TaskForCreatorsCard = styled.div`
  ${CardStyles}
  align-self: stretch;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    box-shadow: none;
    border: none;
    padding: 0;
  }
`

const RateCard = styled.div`
  ${CardStyles}
  align-self: stretch;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    box-shadow: none;
    border: none;
    padding: 0;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`

const CreatorInfoWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;
`

const CreatorNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
`

const Name = styled.span`
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
  max-width: 400px;
`

const Text = styled.span`
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  max-width: 400px;
`

const Title = styled.span`
  color: ${({ theme }) => theme.color.darkGrey};
  font-size: ${variables.fontSize18};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
  flex-grow: 1;
`

const VideoDetailsTitle = styled(Title)`
  margin-bottom: 8px;
`
const StyledSkeleton = styled(({ className, ...rest }: SkeletonNodeProps) => (
  <Skeleton.Input {...rest} rootClassName={className} active />
))`
  &.ant-skeleton-block {
    border-radius: 16px;
  }

  &.ant-skeleton {
    flex-grow: 1 !important;

    @media (max-width: ${MAX_MOBILE_WIDTH}) {
      width: 100% !important;
    }
  }

  & > .ant-skeleton-input {
    width: 100% !important;
    height: 240px !important;
    border-radius: 16px !important;
    background: ${({ theme }) => theme.color.lightGrey} !important;
  }
`

const InfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  width: 100%;
`

const InfoIconWrap = styled.div`
  display: flex;
  align-items: flex-start;
  padding-top: 4px;
`

const InfoText = styled.p`
  flex: 1 0 0;
  font-family: 'Segoe UI', sans-serif;
  font-weight: 400;
  font-size: 14px;
  margin: 0;
  color: ${({ theme }) => theme.color.darkGrey};
  line-height: 150%;
`

interface VideoDetailsProps {
  userPhotoUrl?: string
  firstName?: string
  followers?: number
  currentViews?: number
  minViews?: number
  videoStatus?: VideoStatus
  videoPublishedDate?: string
  milestoneDate?: string
  dismissReason?: EventDismissReasonsEnum
  videoPreModerationStatus?: PremoderationStatus
  tiktokProfileUrl?: string
  isAvailabilityCaseOpenAt?: boolean
  isLoweredViewsCaseOpenAt?: boolean
  commentType?: string | null
  rate?: number
  rateDeadline: string
  onRateSubmit: (value: RateFormData) => void
  isRateLoading?: boolean
  loading?: boolean
  feedback?: string
}

const ADDITIONAL_CHECKS_TEXT =
  "We're completing a few additional checks on this video. Don't worry - stats will show as soon as we're done. 🙌"
export const VideoDetails: FC<VideoDetailsProps> = ({
  userPhotoUrl,
  firstName,
  followers,
  videoStatus,
  currentViews = 0,
  minViews = 0,
  videoPublishedDate = '',
  milestoneDate = '',
  dismissReason,
  loading = false,
  tiktokProfileUrl,
  videoPreModerationStatus,
  isAvailabilityCaseOpenAt,
  isLoweredViewsCaseOpenAt,
  commentType,
  rateDeadline,
  onRateSubmit,
  rate,
  isRateLoading,
  feedback,
}) => {
  const hasPendingChecks =
    Boolean(isAvailabilityCaseOpenAt) || Boolean(isLoweredViewsCaseOpenAt)

  const formattedFollowers = followers ? addSpaceToNumber(followers, ',') : '-'

  const hasTask = useMemo(
    () => Boolean(commentType && tasks.find((t) => t.value === commentType)),
    [commentType],
  )

  if (loading) {
    return <StyledSkeleton />
  }

  if (!videoStatus) {
    return null
  }

  const renderVideoContent = (): ReactNode => (
    <div>
      <TitleWrapper>
        <VideoDetailsTitle>Video details</VideoDetailsTitle>
        <VideoStatusBadge videoPreModerationStatus={videoPreModerationStatus} />
      </TitleWrapper>

      {hasPendingChecks ? (
        <InfoRow>
          <InfoIconWrap>
            <InfoIcon width={16} height={16} />
          </InfoIconWrap>
          <InfoText>{ADDITIONAL_CHECKS_TEXT}</InfoText>
        </InfoRow>
      ) : (
        <ViewsProgressBar
          currentViews={currentViews}
          minViews={minViews}
          videoPublishedDate={videoPublishedDate}
          milestoneDate={milestoneDate}
          status={videoStatus}
          dismissReason={dismissReason}
        />
      )}
    </div>
  )

  const renderRateBlock = (): ReactNode => {
    if (hasPendingChecks) {
      return null
    }

    return (
      <RateBlock
        rateDeadline={rateDeadline}
        onSubmit={onRateSubmit}
        rate={rate}
        feedback={feedback}
        isRateLoading={isRateLoading}
        loading={loading}
      />
    )
  }

  return (
    <Container>
      <HeaderWrapper>
        <CreatorInfoWrapper>
          <AvatarImage
            width={40}
            height={40}
            alt='avatar'
            src={userPhotoUrl}
            fallbackSrc={avatar}
          />
          <CreatorNameWrapper>
            <Name>{firstName || '-'}</Name>
            <Text>{formattedFollowers} followers</Text>
          </CreatorNameWrapper>
        </CreatorInfoWrapper>

        {tiktokProfileUrl && (
          <a href={tiktokProfileUrl} target='_blank' rel='noopener noreferrer'>
            <PlatformButton platform={CampaignPlatformTypeEnum.TIKTOK} />
          </a>
        )}
      </HeaderWrapper>

      <VideoDetailsContainer>{renderVideoContent()}</VideoDetailsContainer>

      {hasTask && (
        <TaskForCreatorsCard>
          <TaskForCreators commentType={commentType} />
        </TaskForCreatorsCard>
      )}

      {!hasPendingChecks && <RateCard>{renderRateBlock()}</RateCard>}
    </Container>
  )
}
